import { useState, useEffect } from 'react';
import { Card, CardBody, CardFooter, Col, Form, FormGroup, Input, Label, Row, Spinner } from 'reactstrap'
import { Btn } from '../../../../../AbstractElements'
import { Country, Emailaddress, Username } from '../../../../../utils/Constant'
import CommonCardHeader from '../../../../../CommonElements/CommonCardHeader/CommonCardHeader'
import { useAuth } from '../../../../../hooks/useAuth'
import UserService from '../../../../../Api/services/user.service'
import CountryService from '../../../../../Api/services/country.service'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import type { components } from '../../../../../Api/types/openapi'

type UpdateUser = components['schemas']['UpdateUser'];
type CountryResponse = components['schemas']['CountryResponse'];

const EditProfileForm = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState<CountryResponse[]>([]);
  const [formData, setFormData] = useState<Partial<UpdateUser>>({
    name: '',
    email: '',
    country_code_phone: '',
    phone: '',
    country: null,
    username: null,
    language: 'ES',
  });

  // Cargar países al montar el componente
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const activeCountries = await CountryService.getAllActiveCountries();
        setCountries(activeCountries);
      } catch (error) {
        console.error('Error al cargar países:', error);
      }
    };
    loadCountries();
  }, []);

  // Cargar datos del usuario al montar
  useEffect(() => {
    if (user) {
      setFormData({
        id: user.id,
        name: user.name || '',
        email: user.email || '',
        country_code_phone: user.country_code_phone || '',
        phone: user.phone || '',
        country: user.country?.id || null,
        username: user.username || null,
        role: user.role?.id || 0,
        language: user.language || 'ES',
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'country' ? (value ? parseInt(value) : null) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar campos requeridos según el schema UpdateUser
    if (!formData.id) {
      toast.error('Error: ID de usuario no encontrado');
      return;
    }
    
    if (!formData.name || !formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    if (!formData.email || !formData.email.trim()) {
      toast.error('El email es requerido');
      return;
    }
    
    if (!formData.country_code_phone || !formData.country_code_phone.trim()) {
      toast.error('El código de país del teléfono es requerido');
      return;
    }
    
    if (!formData.phone || !formData.phone.trim()) {
      toast.error('El teléfono es requerido');
      return;
    }
    
    if (!formData.role || formData.role === 0) {
      toast.error('El rol es requerido');
      return;
    }

    try {
      setIsLoading(true);
      // Preparar payload según el schema UpdateUser que requiere id, role, username
      // Asegurar que country sea number o null (no string)
      const countryValue = typeof formData.country === 'number' 
        ? formData.country 
        : null;
      
      // Asegurar que language sea 'EN' o 'ES' o null
      const languageValue = (formData.language === 'EN' || formData.language === 'ES') 
        ? formData.language 
        : null;
      
      const payload: UpdateUser = {
        id: formData.id!,
        name: formData.name!.trim(),
        email: formData.email!.trim(),
        country_code_phone: formData.country_code_phone!.trim(),
        phone: formData.phone!.trim(),
        country: countryValue,
        role: formData.role!,
        username: formData.username || null,
        ...(languageValue && { language: languageValue }),
      };
      
      console.log('Payload enviado:', JSON.stringify(payload, null, 2));
      console.log('Tipos de datos:', {
        name: typeof payload.name,
        email: typeof payload.email,
        country_code_phone: typeof payload.country_code_phone,
        phone: typeof payload.phone,
        country: typeof payload.country,
        language: typeof payload.language,
      });
      console.log('Valores originales de formData:', {
        country: formData.country,
        countryType: typeof formData.country,
        language: formData.language,
        languageType: typeof formData.language,
      });
      
      await UserService.updateProfile(payload);
      toast.success('Perfil actualizado exitosamente');
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error: any) {
      console.error('Error al actualizar perfil:', error);
      console.error('Response data:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Error al actualizar perfil';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <Col xl="8">
        <Card>
          <CardBody className="text-center">
            <Spinner color="primary" />
            <p className="mt-2">Cargando datos del usuario...</p>
          </CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <Col xl="8">
      <Form onSubmit={handleSubmit}>
        <Card>
          <CommonCardHeader title={t("Editar Perfil")} tagClass={"card-title mb-0"} />
          <CardBody>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>{Username}</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Nombre completo"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>{Emailaddress}</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                    required
                  />
                </FormGroup>
              </Col>
              {/* Address temporalmente oculto hasta confirmar con el backend */}
              {/* <Col md="12">
                <FormGroup>
                  <Label>{Address}</Label>
                  <Input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Dirección"
                  />
                </FormGroup>
              </Col> */}
              <Col md="4">
                <FormGroup>
                  <Label>Código de País (Teléfono)</Label>
                  <Input
                    type="text"
                    name="country_code_phone"
                    value={formData.country_code_phone}
                    onChange={handleChange}
                    placeholder="Ej: MX, US"
                    maxLength={3}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>Teléfono</Label>
                  <Input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Número de teléfono"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>{Country}</Label>
                  <Input
                    type="select"
                    name="country"
                    value={formData.country || ''}
                    onChange={handleChange}
                    className="btn-square form-select"
                  >
                    <option value="">Seleccionar país</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name} ({country.code})
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Idioma</Label>
                  <Input
                    type="select"
                    name="language"
                    value={formData.language || 'ES'}
                    onChange={handleChange}
                    className="btn-square form-select"
                  >
                    <option value="ES">Español</option>
                    <option value="EN">English</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
          <CardFooter className="text-end">
            <Btn color="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("Guardando...")}
                </>
              ) : (
                t("Actualizar Perfil")
              )}
            </Btn>
          </CardFooter>
        </Card>
      </Form>
    </Col>
  )
}

export default EditProfileForm